import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import CoverageApi, {
  CourtCoverageDto,
  CoverageSuggestionDto,
  CoveringAttorneyDto,
} from '../../api/CoverageApi';

/**
 * Who is covering this setting.
 *
 * Lloyd, Aug 21: *"a database for attorneys who have covered various courts"*. The table has existed
 * since then and the only way to add to it was a court and a date, which cannot say which of two
 * settings on the same day somebody actually attended. This records it against the setting itself.
 *
 * ## It is recorded once and not edited
 *
 * A coverage row is an event: this person stood up here. There is no endpoint to change or remove
 * one, on purpose, because the history is the asset — the firm never kept it before. So once a
 * setting names somebody, this shows the name and stops offering the picker. A wrong entry is
 * corrected the way the backend allows: record the right one, and deactivate whoever was entered by
 * mistake.
 */

interface CoverageControlProps {
  courtEventGuid: string;
  /** The appearance already recorded against this setting, if there is one. */
  coverage?: CourtCoverageDto;
  /** Active attorneys, loaded once by the page rather than once per row. */
  attorneys: CoveringAttorneyDto[];
  /**
   * The case's court, for "who could cover this?".
   *
   * Absent on a case with no court on file, which is when the suggestion is least answerable — the
   * rule works off which courts an attorney has appeared at.
   */
  courtGuid?: string | null;
  /** True when this setting requires somebody to attend. Drives the prompt, not the picker. */
  appearanceRequired?: boolean;
  onRecorded: () => void;
  disabled?: boolean;
}

const CoverageControl: React.FC<CoverageControlProps> = ({
  courtEventGuid,
  coverage,
  attorneys,
  courtGuid,
  appearanceRequired = false,
  onRecorded,
  disabled = false,
}) => {
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<CoverageSuggestionDto[] | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  const suggest = async () => {
    if (!courtGuid) return;
    setSuggesting(true);
    setError(null);
    try {
      setSuggestions(await CoverageApi.suggestionsFor(courtGuid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suggestions');
    } finally {
      setSuggesting(false);
    }
  };

  const record = async () => {
    setSaving(true);
    setError(null);
    try {
      await CoverageApi.recordCoverage({
        CoveringAttorney_guid: choice,
        CourtEvent_guid: courtEventGuid,
      });
      setChoice('');
      onRecorded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the coverage');
    } finally {
      setSaving(false);
    }
  };

  if (coverage) {
    return (
      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Covered by
        </Typography>
        <Chip
          size="small"
          label={coverage.attorneyName ?? 'Unknown attorney'}
          color="primary"
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {/* A recovered record is evidence that coverage was arranged, not that it happened. */}
          {coverage.CourtCoverage_Source === 'mailbox'
            ? 'recovered from court correspondence'
            : 'entered by staff'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Covered by
      </Typography>

      {/*
        "Not recorded", never blank. A row that says nothing is read as "nobody needed to cover
        this"; the whole reason the table exists is that the firm never wrote this down, so the
        absence has to be visible.
      */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Not recorded
        {attorneys.length === 0
          ? ' — no covering attorneys on file yet. Add them on the Covering attorneys screen.'
          : ''}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {/* Only where somebody actually has to turn up. On the 95%+ of settings where nobody does,
          an unanswered coverage question is not a problem and saying so would be noise. */}
      {appearanceRequired && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip size="small" color="warning" variant="outlined" label="Needs cover" />
          {courtGuid && (
            <Button size="small" disabled={suggesting} onClick={suggest}>
              {suggesting ? 'Looking…' : 'Who could cover this?'}
            </Button>
          )}
        </Box>
      )}

      {suggestions !== null && (
        <Box sx={{ mb: 1 }}>
          {suggestions.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Nobody has covered this court or one near it. The rule refuses rather than guessing.
            </Typography>
          ) : (
            suggestions.map((suggestion) => (
              <Typography
                key={suggestion.attorneyGuid}
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {/* The reason is always shown: this decides who is sent to a courtroom, so nobody
                    should have to take it on trust. */}
                {suggestion.attorneyName} — {suggestion.reason}
              </Typography>
            ))
          )}
        </Box>
      )}

      {/* Nothing to pick from is not the same as nobody covering, and an empty dropdown reads as
          the second. The line above says which it is. */}
      {attorneys.length > 0 && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <Select
            displayEmpty
            value={choice}
            disabled={disabled || saving}
            onChange={(e) => setChoice(e.target.value)}
          >
            <MenuItem value="">
              <em>Nobody recorded</em>
            </MenuItem>
            {attorneys.map((attorney) => (
              <MenuItem
                key={attorney.CoveringAttorney_guid}
                value={attorney.CoveringAttorney_guid}
              >
                {attorney.CoveringAttorney_Name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" disabled={disabled || saving || !choice} onClick={record}>
          Record
        </Button>
      </Box>
      )}
    </Box>
  );
};

export default CoverageControl;
